import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  BenefitsList,
  ConfirmPasswordMatch,
  ErrorMessage,
  Eyebrow,
  FormCard,
  FormContent,
  FormDivider,
  FormRow,
  FormTitle,
  FullWidthField,
  Heading,
  Intro,
  IntroLinks,
  Lead,
  PasswordStrength,
  PasswordStrengthBar,
  PasswordStrengthLabel,
  SignUpPanel,
  SubmitButton,
} from './SignUp.styled';
import LoadingOverlay from '../common/LoadingOverlay';
import { IconButton, Checkbox, FormControlLabel, TextField } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { accountService } from '../../services/api/accountService';
import { formatPhoneInputMask, phoneDigitsForApi } from '../../utils/phoneInputMask';
import {
  getSignupEmailError,
  getSignupPhoneError,
} from '../../utils/signupFieldValidation';
import {
  getPasswordStrength,
  isPasswordValid,
  PASSWORD_REQUIREMENTS_HELPER_TEXT,
} from '../../utils/passwordRules';
import { useAuth } from '../../context/AuthContext';
import { getLoginReturnPathFromLocation } from '../../utils/authSessionExpired';
import { toPersonNameCase } from '../../utils/normalizePersonName';

const SignUp = () => {
  const [formData, setFormData] = useState({
    FirstName: '',
    LastName: '',
    Email: '',
    PhoneNumber: '',
    DateOfBirth: '',
    Password: '',
    VerifyPassword: '',
    isBusinessUser: false,
    agreedToTerms: false,
  });

  const [errors, setErrors] = useState({
    FirstName: '',
    LastName: '',
    Email: '',
    PhoneNumber: '',
    DateOfBirth: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const returnTo = getLoginReturnPathFromLocation(location);

  const doPasswordsMatch = () =>
    formData.Password === formData.VerifyPassword && formData.Password !== '';

  const validateDateOfBirth = (dob) => {
    if (!dob) return '';
    const dobDate = new Date(dob);
    const minDate = new Date('1920-01-01');
    const today = new Date();
    const eighteenYearsAgo = new Date(today);
    eighteenYearsAgo.setFullYear(today.getFullYear() - 18);

    if (dobDate < minDate || dobDate > today) {
      return 'Please enter a valid date of birth';
    }
    if (dobDate >= eighteenYearsAgo) {
      return 'You must be at least 18 years old to create an account';
    }
    return '';
  };

  const validateName = (name) => {
    if (!name || !String(name).trim()) {
      return 'Name is required';
    }
    return '';
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    if (name === 'Password') {
      setPasswordTouched(true);
    }
    if (name === 'VerifyPassword') {
      setConfirmTouched(true);
    }

    if (name === 'FirstName' || name === 'LastName') {
      const normalized = toPersonNameCase(value);
      if (normalized !== value) {
        setFormData((prev) => ({ ...prev, [name]: normalized }));
      }
    }

    let error = '';

    switch (name) {
      case 'FirstName':
      case 'LastName':
        error = validateName(toPersonNameCase(value));
        break;
      case 'Email':
        error = getSignupEmailError(value);
        break;
      case 'PhoneNumber':
        error = getSignupPhoneError(value);
        break;
      case 'DateOfBirth':
        error = validateDateOfBirth(value);
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    } else if (name === 'PhoneNumber') {
      setFormData((prev) => ({
        ...prev,
        [name]: formatPhoneInputMask(value),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (name === 'FirstName' && errors.FirstName !== '') {
      setErrors((prev) => ({ ...prev, FirstName: '' }));
    } else if (name === 'LastName' && errors.LastName !== '') {
      setErrors((prev) => ({ ...prev, LastName: '' }));
    } else if (name === 'PhoneNumber' && errors.PhoneNumber !== '') {
      setErrors((prev) => ({ ...prev, PhoneNumber: '' }));
    } else if (name === 'Email' && errors.Email !== '') {
      setErrors((prev) => ({ ...prev, Email: '' }));
    } else if (name === 'DateOfBirth' && errors.DateOfBirth !== '') {
      setErrors((prev) => ({ ...prev, DateOfBirth: '' }));
    }
  };

  const isFormValid = () => {
    const hasErrors = Object.values(errors).some((error) => error !== '');
    const requiredFields = {
      FirstName: formData.FirstName,
      LastName: formData.LastName,
      Email: formData.Email,
      PhoneNumber: formData.PhoneNumber,
      DateOfBirth: formData.DateOfBirth,
      Password: formData.Password,
      VerifyPassword: formData.VerifyPassword,
    };

    const hasEmptyFields = Object.entries(requiredFields).some(([key, value]) => {
      if (key === 'PhoneNumber') {
        return (
          !value ||
          !String(value).trim() ||
          !phoneDigitsForApi(value) ||
          phoneDigitsForApi(value).length !== 10
        );
      }
      return !value || !String(value).trim();
    });

    return (
      !hasErrors &&
      !hasEmptyFields &&
      !getSignupEmailError(formData.Email) &&
      !getSignupPhoneError(formData.PhoneNumber) &&
      isPasswordValid(formData.Password) &&
      doPasswordsMatch() &&
      formData.agreedToTerms
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setIsLoading(true);

    try {
      const role = formData.isBusinessUser ? 'business_user' : 'user';

      await accountService.createAccount({
        FirstName: toPersonNameCase(formData.FirstName),
        LastName: toPersonNameCase(formData.LastName),
        Email: formData.Email,
        PhoneNumber: phoneDigitsForApi(formData.PhoneNumber),
        DateOfBirth: formData.DateOfBirth,
        Password: formData.Password,
        Role: role,
      });

      await login(formData.Email, formData.Password);
      navigate(returnTo, { replace: true });
    } catch (error) {
      console.error(
        'There was a problem with the create account operation:',
        error,
      );
      if (error.status === 409) {
        setSubmitError(
          'An account with this email already exists. Please sign in instead, or use a different email.',
        );
      } else {
        setSubmitError(
          'There was an error during registration. Please try again.',
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowVerifyPassword = () =>
    setShowVerifyPassword((show) => !show);

  const pwStrength = getPasswordStrength(formData.Password);
  const passwordHelperText =
    errors.Password ||
    (passwordTouched && !isPasswordValid(formData.Password)
      ? PASSWORD_REQUIREMENTS_HELPER_TEXT
      : '');
  const confirmMismatch =
    confirmTouched &&
    formData.VerifyPassword.length > 0 &&
    !doPasswordsMatch();

  return (
    <SignUpPanel aria-labelledby="sign-up-title">
      <Intro>
        <Eyebrow>Create an account</Eyebrow>
        <Heading id="sign-up-title">Join NKY Connect</Heading>
        <Lead>
          It&apos;s free, and it lets you use smart search to match your job with the right
          local pro.
        </Lead>

        <BenefitsList>
          <li>
            <CheckRoundedIcon aria-hidden />
            Only Northern Kentucky businesses
          </li>
          <li>
            <CheckRoundedIcon aria-hidden />
            Smart search for jobs that are hard to describe
          </li>
          <li>
            <CheckRoundedIcon aria-hidden />
            Your info goes only to the pro you pick
          </li>
        </BenefitsList>

        <IntroLinks>
          <p>
            Already have an account?{' '}
            <Link to="/login" state={{ from: returnTo }}>
              Sign in
            </Link>
          </p>
          <p>
            Run a home-service business?{' '}
            <Link to="/provider-signup">Join as a pro</Link>
          </p>
        </IntroLinks>
      </Intro>

      <FormCard>
        <FormTitle>Your details</FormTitle>
        <form onSubmit={handleSubmit}>
          <FormContent>
            <FormRow>
              <TextField
                label="First Name"
                size="small"
                name="FirstName"
                value={formData.FirstName}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                fullWidth
                autoComplete="given-name"
                error={!!errors.FirstName}
                helperText={errors.FirstName}
              />
              <TextField
                label="Last Name"
                size="small"
                name="LastName"
                value={formData.LastName}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                fullWidth
                autoComplete="family-name"
                error={!!errors.LastName}
                helperText={errors.LastName}
              />
            </FormRow>

            <FormRow>
              <TextField
                label="Email (this will be your username)"
                size="small"
                name="Email"
                type="email"
                value={formData.Email}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                fullWidth
                autoComplete="email"
                error={!!errors.Email}
                helperText={errors.Email}
              />
            </FormRow>

            <FormRow>
              <TextField
                label="Phone Number"
                size="small"
                name="PhoneNumber"
                type="tel"
                value={formData.PhoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                fullWidth
                placeholder="555-123-4567"
                inputProps={{ inputMode: 'numeric', autoComplete: 'tel' }}
                error={!!errors.PhoneNumber}
                helperText={errors.PhoneNumber}
              />
              <TextField
                label="Date of Birth"
                size="small"
                name="DateOfBirth"
                type="date"
                value={formData.DateOfBirth}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                fullWidth
                error={!!errors.DateOfBirth}
                helperText={errors.DateOfBirth}
                InputLabelProps={{ shrink: true }}
              />
            </FormRow>

            <FormDivider />

            <FormRow>
              <FullWidthField>
                <TextField
                  label="Password"
                  size="small"
                  name="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.Password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  fullWidth
                  autoComplete="new-password"
                  error={
                    passwordTouched &&
                    (!isPasswordValid(formData.Password) || !!errors.Password)
                  }
                  helperText={passwordHelperText}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={handleClickShowPassword}
                        edge="end"
                        size="small"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
                {formData.Password && (
                  <PasswordStrength>
                    <PasswordStrengthBar
                      $score={pwStrength.score}
                      $maxScore={pwStrength.maxScore}
                      $color={pwStrength.color}
                    />
                    <PasswordStrengthLabel $color={pwStrength.color}>
                      {pwStrength.label}
                    </PasswordStrengthLabel>
                  </PasswordStrength>
                )}
              </FullWidthField>
            </FormRow>
            <FormRow>
              <FullWidthField>
                <TextField
                  label="Confirm Password"
                  size="small"
                  name="VerifyPassword"
                  type={showVerifyPassword ? 'text' : 'password'}
                  value={formData.VerifyPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  fullWidth
                  autoComplete="new-password"
                  placeholder="Re-enter your password"
                  error={confirmMismatch}
                  helperText={confirmMismatch ? 'Passwords do not match' : ''}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={handleClickShowVerifyPassword}
                        edge="end"
                        size="small"
                        aria-label={
                          showVerifyPassword ? 'Hide confirm password' : 'Show confirm password'
                        }
                      >
                        {showVerifyPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
                {formData.VerifyPassword.length > 0 &&
                  doPasswordsMatch() &&
                  !confirmMismatch && (
                    <ConfirmPasswordMatch role="status" aria-live="polite">
                      <CheckCircleIcon sx={{ fontSize: 18 }} aria-hidden />
                      Your passwords match.
                    </ConfirmPasswordMatch>
                  )}
              </FullWidthField>
            </FormRow>

            <FormRow>
              <FormControlLabel
                sx={{ alignItems: 'center', marginLeft: 0, gap: '6px' }}
                control={
                  <Checkbox
                    checked={formData.agreedToTerms}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        agreedToTerms: e.target.checked,
                      }))
                    }
                    name="agreedToTerms"
                    size="small"
                    sx={{ p: '4px' }}
                  />
                }
                label={
                  <span>
                    I agree to the{' '}
                    <Link to="/terms" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </Link>
                  </span>
                }
              />
            </FormRow>
          </FormContent>

          {submitError && (
            <ErrorMessage role="alert" aria-live="assertive">
              {submitError}
            </ErrorMessage>
          )}

          <SubmitButton type="submit" disabled={!isFormValid() || isLoading}>
            {isLoading ? 'Creating account…' : 'Create account'}
          </SubmitButton>
        </form>
      </FormCard>
      {isLoading && <LoadingOverlay />}
    </SignUpPanel>
  );
};

export default SignUp;
